import React, { useCallback, useState } from 'react';
import { LegacyCard as Card, Select, TextField } from '@shopify/polaris';
import { EG_CITIES } from '../../../constants/storefrontOrderConstants';

/**
 * Customer details card of the order form, mirroring the Shopify
 * storefront order form fields and their validation messages.
 *
 * Errors only surface once a field has been blurred (touched) — unless
 * forceShowErrors is true, e.g. after a submit attempt.
 *
 * @param {Object} props
 * @param {Object} props.customer - { name, phone, email, address, city }.
 * @param {Object} props.errors - Field name to error message map (from validateCustomer).
 * @param {Function} props.onChange - Receives (field, value).
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.forceShowErrors] - Show every error regardless of touched state.
 * @returns {JSX.Element}
 */
export default function CustomerCard({
  customer,
  errors,
  onChange,
  disabled = false,
  forceShowErrors = false
}) {
  const [touched, setTouched] = useState({});

  const handle = useCallback(
    field => value => {
      onChange(field, value);
    },
    [onChange]
  );

  const handleBlur = useCallback(field => () => {
    setTouched(current => ({ ...current, [field]: true }));
  }, []);

  const showError = field => (forceShowErrors || touched[field] ? errors[field] : undefined);

  const cityOptions = [
    { value: '', label: 'Choose city', disabled: true },
    ...EG_CITIES.map(city => ({ value: city, label: city }))
  ];

  return (
    <Card>
      <Card.Section title="Customer">
        <TextField
          label="Full name"
          value={customer.name || ''}
          onChange={handle('name')}
          onBlur={handleBlur('name')}
          error={showError('name')}
          disabled={disabled}
          autoComplete="off"
        />
        <TextField
          label="Phone number"
          value={customer.phone || ''}
          onChange={handle('phone')}
          onBlur={handleBlur('phone')}
          error={showError('phone')}
          disabled={disabled}
          autoComplete="off"
        />
        <TextField
          label="Email"
          value={customer.email || ''}
          onChange={handle('email')}
          onBlur={handleBlur('email')}
          error={showError('email')}
          disabled={disabled}
          autoComplete="off"
        />
        <TextField
          label="Address"
          value={customer.address || ''}
          onChange={handle('address')}
          onBlur={handleBlur('address')}
          error={showError('address')}
          disabled={disabled}
          autoComplete="off"
        />
        <Select
          label="City"
          options={cityOptions}
          value={customer.city || ''}
          onChange={handle('city')}
          onBlur={handleBlur('city')}
          error={showError('city')}
          disabled={disabled}
        />
      </Card.Section>
    </Card>
  );
}
